import { useEffect, useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EntityDrawer,
  FormError,
  Input,
  Label,
  MacroInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TagInput,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { TrafficSource, TrafficSourceStatus, type TrafficSource as TrafficSourceT } from "@gen/tracker/v1/traffic_source_pb";
import type { Click } from "@gen/tracker/v1/click_pb";

import { plural, useT } from "@/lib/i18n";
import { DASH, formatAge, formatInt } from "@/lib/format";
import { rangeForPreset } from "@/lib/period";
import { SOURCE_TYPES, adParams, getSourceType, isValidMacroKey, kindFromProto, kindToProto, type AdParam, type TrafficSourceKindString } from "@/lib/source-types";
import { splitMacros, trafficSourceFormSchema } from "@/lib/schemas/traffic-source";
import { useSearchClicks } from "@/lib/services/click-service";
import { useArchiveTrafficSource, useCreateTrafficSource, useUpdateTrafficSource } from "@/lib/services/traffic-source-service";
import { useReportTimezone } from "@/lib/services/workspace-service";

import { addParam, aliasMap, aliasPairs, missingParams, paramMeaning, paramWords, removeParam, subNumber, templateParams } from "./click-params";


/** The click id a click carries under the platform's own parameter. */
function clickIdOf(click: Click | undefined, param: string): string | undefined {
  if (!click) return undefined;
  const v = param === "fbclid" ? click.fbclid : param === "gclid" ? click.gclid : param === "ttclid" ? click.ttclid : click.clickidExternal;
  return v || undefined;
}

/**
 * Источник (доска SourceEditor): one EntityDrawer over the list for a new
 * source and for an existing one — the platform, the name, the ad-link
 * template on `MacroInput` with the platform's macros as chips, «Что пишется
 * в клик» — the template's parameters one row each: the sub, what it is in a
 * person's words, the platform's macro and an example from the source's
 * latest click — then the other names the edge reads into a sub, and where
 * spend comes from. Field names are written as they are (Label
 * textCase="none"), words in a sans field, the template and the tags mono.
 * «В архив» sits apart on the left; a source that campaigns use says how
 * many, because a change applies to their new clicks.
 */
export function SourceEditor({
  open,
  onOpenChange,
  source,
  campaigns,
  spend,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: TrafficSourceT | null;
  /** How many campaigns run on it, from the server's fold; undefined while unknown. */
  campaigns: number | undefined;
  /** Seconds since vibe-fb last pushed spend, undefined when it never did. */
  spend: { age: number; ads: number } | undefined;
}) {
  const t = useT();
  const create = useCreateTrafficSource();
  const update = useUpdateTrafficSource();
  const archive = useArchiveTrafficSource();
  const tz = useReportTimezone();
  const [kind, setKind] = useState<TrafficSourceKindString>("FACEBOOK_ADS");
  const [name, setName] = useState("");
  const [template, setTemplate] = useState("");
  const [aliases, setAliases] = useState<Record<string, string>>({});
  const [costMacros, setCostMacros] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [archiving, setArchiving] = useState(false);

  // Each opening starts from the source (or from the platform's defaults).
  useEffect(() => {
    if (!open) return;
    const k = source ? kindFromProto(source.kind) : "FACEBOOK_ADS";
    const spec = getSourceType(k);
    setKind(k);
    setName(source?.name ?? spec.displayName);
    setTemplate(source?.clickUrlTemplate ?? spec.defaultClickUrl);
    setAliases(aliasPairs(source?.trackingFieldMap ?? spec.defaultTrackingMap));
    setCostMacros((source?.costMacros ?? spec.defaultMacros).join(", "));
    setErrors({});
  }, [open, source]);

  // «Пример»: what the source's latest click of the last 7 days carried — the server's row, nothing made up.
  const week = useMemo(() => rangeForPreset("7d", new Date(), tz), [tz]);
  const latest = useSearchClicks({ range: { ...week, timezone: tz }, trafficSourceId: source?.id, limit: 1 }, { enabled: open && !!source });
  const sample = source ? latest.data?.items[0] : undefined;

  const spec = getSourceType(kind);
  const params = adParams(kind);
  const macros = useMemo(
    () => [
      ...(spec.clickIdParam ? [{ value: `{${spec.clickIdParam}}`, description: t("ops.src.clickId") }] : []),
      ...params.map((p) => ({ value: p.macro, description: p.label })),
    ],
    [spec.clickIdParam, params, t],
  );
  const written = templateParams(template);
  const clickIdRow = spec.clickIdParam ? (written.find((p) => p.param === spec.clickIdParam) ?? { param: spec.clickIdParam, macro: `{${spec.clickIdParam}}` }) : undefined;
  const rows = written.filter((p) => p.param !== spec.clickIdParam).sort((a, b) => (subNumber(a.param) ?? 99) - (subNumber(b.param) ?? 99));
  const missing = missingParams(template, params, spec.defaultTrackingMap).filter((p) => p.param !== spec.clickIdParam);
  const meaning = (p: AdParam | undefined) => (p ? paramWords(p.label, t) : t("ops.src.own"));

  const pickKind = (k: TrafficSourceKindString) => {
    const next = getSourceType(k);
    setKind(k);
    // A new source takes the platform's defaults; an existing one keeps what the buyer set.
    if (!source) {
      setName(next.displayName);
      setTemplate(next.defaultClickUrl);
      setAliases(aliasPairs(next.defaultTrackingMap));
      setCostMacros(next.defaultMacros.join(", "));
    }
  };

  const save = async () => {
    const parsed = trafficSourceFormSchema.safeParse({ name, clickUrlTemplate: template, costMacros, trackingFieldMap: aliasMap(aliases) });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    const payload = new TrafficSource({
      ...(source ?? {}),
      id: source?.id ?? "",
      name: parsed.data.name,
      kind: kindToProto(kind),
      clickUrlTemplate: parsed.data.clickUrlTemplate,
      trackingFieldMap: parsed.data.trackingFieldMap,
      costMacros: splitMacros(parsed.data.costMacros),
      status: source?.status ?? TrafficSourceStatus.ACTIVE,
    });
    try {
      await (source ? update : create).mutateAsync(payload);
      toast.success(t("ops.src.saved", { name: payload.name }));
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("lib.sources.saveFailed"));
    }
  };

  const used = campaigns ?? 0;
  const facebook = kind === "FACEBOOK_ADS";
  return (
    <>
      <EntityDrawer
        open={open}
        onOpenChange={onOpenChange}
        width="2xl"
        title={source ? t("ops.src.editTitle", { name: source.name }) : t("ops.src.newTitle")}
        description={source && campaigns !== undefined ? (used > 0 ? t("ops.src.used", { count: formatInt(used), campaigns: plural(t("word.campaigns"), used) }) : t("ops.src.unused")) : undefined}
        primaryAction={{ label: t("lib.common.save"), onClick: save, loading: create.isPending || update.isPending, loadingLabel: t("lib.common.saving") }}
        secondaryAction={{ label: t("ops.cancel"), onClick: () => onOpenChange(false) }}
        destructiveAction={source ? { label: t("lib.common.archive"), onClick: () => setArchiving(true) } : undefined}
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="src-kind" textCase="none">
                {t("ops.src.platform")}
              </Label>
              {/* The platform is a word, not data: the package's Select (sans), not the mono NativeSelect. */}
              <Select value={kind} onValueChange={(v) => pickKind(v as TrafficSourceKindString)}>
                <SelectTrigger id="src-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((s) => (
                    <SelectItem key={s.kind} value={s.kind}>
                      {s.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="src-name" textCase="none">
                {t("lib.common.col.name")}
              </Label>
              <Input id="src-name" font="sans" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} aria-invalid={!!errors.name} />
              {errors.name ? <FormError message={errors.name} /> : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="src-template" textCase="none">
              {t("ops.src.template")}
            </Label>
            <MacroInput
              id="src-template"
              value={template}
              onChange={setTemplate}
              macros={macros}
              macrosLabel={t("ops.src.macros")}
              macrosPosition="above"
              hint={t("ops.src.template.hint")}
              error={!!errors.clickUrlTemplate}
              className="font-mono"
            />
          </div>

          <section className="flex flex-col gap-2" aria-label={t("ops.src.fields")}>
            <div className="flex items-center">
              <h3 className="text-label text-muted-foreground">{t("ops.src.fields")}</h3>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="ml-auto" disabled={missing.length === 0} title={missing.length === 0 ? t("ops.src.add.none") : undefined}>
                    <Plus className="size-3.5" />
                    {t("ops.src.add")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {missing.map((p) => (
                    <DropdownMenuItem key={p.macro} onSelect={() => setTemplate((v) => addParam(v, p))}>
                      <span>{meaning(p)}</span>
                      <span className="ml-auto pl-4 font-mono text-xs text-muted-foreground">{p.macro}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <table className="w-full table-fixed text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="w-28 py-1.5 text-left font-normal">{t("ops.src.col.param")}</th>
                  <th className="py-1.5 text-left font-normal">{t("ops.src.col.what")}</th>
                  <th className="py-1.5 text-left font-normal">{t("ops.src.col.macro")}</th>
                  <th className="py-1.5 text-left font-normal" title={t("ops.src.example.hint")}>
                    {t("ops.src.col.example")}
                  </th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {clickIdRow ? (
                  <tr className="h-9 border-b border-border">
                    <td className="font-mono text-xs">{t("ops.src.clickId")}</td>
                    <td>{clickIdRow.param}</td>
                    <td className="truncate pr-2 font-mono text-xs">{clickIdRow.macro}</td>
                    <Example value={clickIdOf(sample, clickIdRow.param)} />
                    <td />
                  </tr>
                ) : null}
                {rows.map((r) => (
                  <tr key={r.param} className="h-9 border-b border-border">
                    <td className="font-mono text-xs">{r.param}</td>
                    <td className="truncate pr-2">{meaning(paramMeaning(r, params, spec.defaultTrackingMap))}</td>
                    <td className="truncate pr-2 font-mono text-xs">{r.macro || DASH}</td>
                    <Example value={sample?.trackingFields[r.param]} />
                    <td className="text-right">
                      <Button variant="ghost" size="icon-sm" aria-label={`${t("ops.src.remove")} ${r.param}`} onClick={() => setTemplate((v) => removeParam(v, r.param))}>
                        <X className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-1 flex flex-col gap-1.5">
              <Label htmlFor="src-aliases" textCase="none">
                {t("ops.src.aliases")}
              </Label>
              <TagInput
                id="src-aliases"
                value={aliases}
                onChange={setAliases}
                placeholder={t("ops.src.aliases.placeholder")}
                validate={(k, v) => (isValidMacroKey(k) && subNumber(v.trim()) ? undefined : t("ops.src.aliases.invalid"))}
              />
              <span className="text-xs text-muted-foreground">{t("ops.src.aliases.hint")}</span>
              {errors.trackingFieldMap ? <FormError message={errors.trackingFieldMap} /> : null}
            </div>
          </section>

          <section className="flex flex-col gap-2" aria-label={t("ops.src.spend")}>
            <h3 className="text-label text-muted-foreground">{t("ops.src.spend")}</h3>
            {facebook ? (
              <div className="rounded-md border border-border px-3.5 py-2.5">
                <div className="text-sm">{t("ops.src.spend.fb")}</div>
                <div className="text-xs text-muted-foreground">
                  {spend ? t("ops.src.spend.fb.last", { age: formatAge(spend.age), ads: formatInt(spend.ads) }) : t("ops.src.spend.fb.never")}
                </div>
              </div>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="src-cost" textCase="none">
                {t("ops.src.costMacros")}
              </Label>
              <Input id="src-cost" value={costMacros} onChange={(e) => setCostMacros(e.target.value)} aria-invalid={!!errors.costMacros} />
              <span className="text-xs text-muted-foreground">{t("ops.src.costMacros.hint")}</span>
              {errors.costMacros ? <FormError message={errors.costMacros} /> : null}
            </div>
            {facebook ? (
              <Alert variant="info">
                <Check className="size-4" />
                <div className="min-w-0">
                  <AlertTitle>{t("ops.src.capi")}</AlertTitle>
                  <AlertDescription>{t("ops.src.capi.body")}</AlertDescription>
                </div>
              </Alert>
            ) : null}
          </section>
        </div>
      </EntityDrawer>
      <ConfirmModal
        open={archiving}
        onClose={() => setArchiving(false)}
        tone="danger"
        loading={archive.isPending}
        title={t("ops.src.archive.title", { name: source?.name ?? "" })}
        description={used > 0 ? t("ops.src.archive.used", { count: formatInt(used), campaigns: plural(t("word.campaigns"), used) }) : t("ops.src.archive.body")}
        confirmLabel={t("lib.common.archive")}
        onConfirm={() => {
          if (!source) return;
          archive.mutate(source.id, {
            onSuccess: () => {
              toast.success(t("ops.src.archived", { name: source.name }));
              setArchiving(false);
              onOpenChange(false);
            },
            onError: (e) => toast.error(e instanceof Error ? e.message : t("lib.sources.saveFailed")),
          });
        }}
      />
    </>
  );
}

/** «Пример»: the latest click's value, cut to the column; a dash when there is none. */
function Example({ value }: { value: string | undefined }) {
  return (
    <td className="truncate pr-2 font-mono text-xs text-muted-foreground" title={value}>
      {value || DASH}
    </td>
  );
}
